import { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { X, Send, Check, Loader2, Calendar } from "lucide-react";

/**
 * Brand demo-request modal — anonymous lead capture from /for-brands.
 *
 * Submits to POST /api/demo-requests (no auth). On success shows a confirmation
 * state; on 429 shows the throttle message; closes via overlay click, X button
 * or Escape.
 */
const VOLUME_OPTIONS = [
  { v: "1-5",   label: "1–5 partnerships / month" },
  { v: "5-20",  label: "5–20 partnerships / month" },
  { v: "20-50", label: "20–50 partnerships / month" },
  { v: "50+",   label: "50+ partnerships / month" },
  { v: "exploring", label: "Just exploring" },
];

export default function DemoModal({ open, onClose }) {
  const [form, setForm] = useState({
    full_name: "", work_email: "", company: "", role: "",
    intent: "", monthly_partnerships: "5-20",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const firstInputRef = useRef(null);

  // Reset internal state every time the modal is freshly opened.
  useEffect(() => {
    if (open) {
      setForm({ full_name: "", work_email: "", company: "", role: "",
                intent: "", monthly_partnerships: "5-20" });
      setErr(""); setDone(false); setBusy(false);
      // small delay so the input is actually mounted before focus
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [open]);

  // Esc-to-close — set up once per open cycle.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      await api.post("/demo-requests", form);
      setDone(true);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-black/80 backdrop-blur-sm"
         data-testid="demo-modal" onClick={onClose}>
      <div className="relative w-full max-w-xl bg-bg-surface border border-line max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-ink-muted hover:text-white"
                aria-label="Close" data-testid="demo-modal-close">
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <SuccessState onClose={onClose} email={form.work_email} />
        ) : (
          <form onSubmit={submit} className="p-8" data-testid="demo-modal-form">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-volt" />
              <div className="label-eyebrow text-volt">Talk to PITCHD</div>
            </div>
            <h2 className="font-heading text-3xl uppercase tracking-tighter">Request a 15-min demo.</h2>
            <p className="text-sm text-ink-muted mt-3">
              Tell us a bit about your team. We'll send a 15-minute walkthrough slot tailored to your campaign volume — usually within one business day.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-7">
              <Field label="Full name" required>
                <input ref={firstInputRef} value={form.full_name} onChange={(e) => upd("full_name", e.target.value)}
                  required minLength={2} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" data-testid="demo-full-name" />
              </Field>
              <Field label="Work email" required>
                <input value={form.work_email} type="email" onChange={(e) => upd("work_email", e.target.value)}
                  required className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" data-testid="demo-work-email"
                  placeholder="you@company.com" />
              </Field>
              <Field label="Company" required>
                <input value={form.company} onChange={(e) => upd("company", e.target.value)}
                  required minLength={2} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" data-testid="demo-company" />
              </Field>
              <Field label="Your role" required>
                <input value={form.role} onChange={(e) => upd("role", e.target.value)}
                  required minLength={2} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" placeholder="e.g. Head of Partnerships"
                  data-testid="demo-role" />
              </Field>
            </div>

            <Field label="Monthly partnership volume" className="mt-4">
              <select value={form.monthly_partnerships}
                onChange={(e) => upd("monthly_partnerships", e.target.value)}
                className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" data-testid="demo-volume">
                {VOLUME_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </Field>

            <Field label="What are you hoping to use PITCHD for?" className="mt-4">
              <textarea value={form.intent} onChange={(e) => upd("intent", e.target.value)}
                rows={3} maxLength={800} className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm resize-none"
                placeholder="One or two sentences — campaign type, timelines, athletes you'd want to reach."
                data-testid="demo-intent" />
            </Field>

            {err && <div className="mt-4 text-xs text-[#FF3B30]" data-testid="demo-error">{err}</div>}

            <div className="flex flex-wrap gap-3 mt-7 items-center">
              <button type="submit" disabled={busy}
                className="btn-volt inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="demo-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "Sending…" : "Request demo"}
              </button>
              <span className="text-[11px] text-ink-muted">
                Or email{" "}
                <a href="mailto:partnerships@pitchdhq.com" className="text-volt hover:text-white">
                  partnerships@pitchdhq.com
                </a>
                {" "}directly.
              </span>
            </div>

            <p className="text-[10px] text-ink-dim mt-5">
              We'll only use this info to schedule your demo and follow up. We never share your details. GDPR-compliant.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, required, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="label-eyebrow block mb-2">
        {label}{required && <span className="text-volt ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SuccessState({ onClose, email }) {
  return (
    <div className="p-10 text-center" data-testid="demo-success">
      <div className="w-14 h-14 mx-auto bg-volt flex items-center justify-center mb-5">
        <Check className="w-7 h-7 text-black" strokeWidth={3} />
      </div>
      <h3 className="font-heading text-3xl uppercase tracking-tighter">Got it. We'll be in touch.</h3>
      <p className="text-sm text-ink-muted mt-4 max-w-md mx-auto leading-relaxed">
        We've logged your request. Someone from the PITCHD team will reach out to{" "}
        <span className="text-white">{email}</span> with a personalised 15-min demo slot — usually within one business day (Mon–Fri).
      </p>
      <button onClick={onClose} className="btn-ghost-line mt-7" data-testid="demo-success-close">
        Close
      </button>
    </div>
  );
}
